import { Controller } from "@hotwired/stimulus"
import * as dagre from "dagre"
import debounce from "lodash.debounce"

// Sugiyama-style layered layout via dagre.
// Drag background to pan, wheel to zoom, Ctrl+0 to fit. Native fullscreen.
// DMs get connection handles on each node — drag from handle to another node to create an edge.
// Node/note drag positions persist to the server (debounced); a minimap gives an overview with click-to-jump.
export default class extends Controller {
  static targets = ["canvas", "svg", "node", "wrapper", "minimap", "minimapViewport"]
  static values  = { edges: Array, notes: Array, notesUrl: String, canConnect: Boolean, createEdgeUrl: String }

  connect() {
    this.scale    = 1
    this.panX     = 0
    this.panY     = 0
    this._panning = false
    this._savePosition = debounce((url, x, y) => this._persistPosition(url, x, y), 400)

    this._onWheel          = e => this.onWheel(e)
    this._onPointerDown    = e => this.onPointerDown(e)
    this._onPointerMove    = e => this.onPointerMove(e)
    this._onPointerUp      = e => this.onPointerUp(e)
    this._onKeyDown        = e => this.onKeyDown(e)
    this._onFSChange       = () => this._handleFullscreenChange()
    this._onCanvasDblClick = e => this.onCanvasDblClick(e)

    if (this.hasWrapperTarget) {
      const w = this.wrapperTarget
      w.addEventListener('wheel',         this._onWheel,       { passive: false })
      w.addEventListener('pointerdown',   this._onPointerDown)
      w.addEventListener('pointermove',   this._onPointerMove)
      w.addEventListener('pointerup',     this._onPointerUp)
      w.addEventListener('pointercancel', this._onPointerUp)
      if (this.canConnectValue) w.addEventListener('dblclick', this._onCanvasDblClick)
      w.style.userSelect = 'none'
    }
    document.addEventListener('keydown',          this._onKeyDown)
    document.addEventListener('fullscreenchange', this._onFSChange)

    if (this.hasCanvasTarget && this.nodeTargets.length > 0) {
      this._observer = new MutationObserver(() => {
        if (!this.element.closest('.hidden')) {
          requestAnimationFrame(() => { this.layout(); this.drawEdges(); this.fitToViewport() })
        }
      })
      this._observer.observe(
        this.element.closest('[data-tabs-target="panel"]') || this.element,
        { attributes: true, attributeFilter: ['class'] }
      )
      if (!this.element.closest('.hidden')) {
        requestAnimationFrame(() => { this.layout(); this.drawEdges(); this.fitToViewport() })
      }

      this.nodeTargets.forEach(el => {
        el.addEventListener('click', e => {
          if (el._dragged) { el._dragged = false; return }
          if (el.dataset.href) window.location.href = el.dataset.href
        })
        el.addEventListener('mousedown', e => this._nodeDragStart(e, el))
        if (this.canConnectValue) this._addConnectHandle(el)
      })
    }
  }

  disconnect() {
    this._observer?.disconnect()
    if (this.hasWrapperTarget) {
      const w = this.wrapperTarget
      w.removeEventListener('wheel',         this._onWheel)
      w.removeEventListener('pointerdown',   this._onPointerDown)
      w.removeEventListener('pointermove',   this._onPointerMove)
      w.removeEventListener('pointerup',     this._onPointerUp)
      w.removeEventListener('pointercancel', this._onPointerUp)
      w.removeEventListener('dblclick',      this._onCanvasDblClick)
    }
    document.removeEventListener('keydown',          this._onKeyDown)
    document.removeEventListener('fullscreenchange', this._onFSChange)
    this._cleanupNodeDrag()
    this._cleanupConnect()
  }

  // --- Value callbacks ---

  edgesValueChanged() {
    if (this._positions) this.drawEdges()
  }

  // --- Zoom / Pan ---

  onWheel(event) {
    if (event.target.closest('[data-node-id]')) return
    if (event.target.closest('.ag-note'))       return
    if (event.target.closest('[data-hud]'))     return
    event.preventDefault()
    const rect   = this.wrapperTarget.getBoundingClientRect()
    const factor = event.deltaY < 0 ? 1.1 : 1 / 1.1
    this._zoomAround(event.clientX - rect.left, event.clientY - rect.top, factor)
  }

  _zoomAround(lx, ly, factor) {
    const newScale = Math.max(0.12, Math.min(4, this.scale * factor))
    const ratio    = newScale / this.scale
    this.panX  = lx - ratio * (lx - this.panX)
    this.panY  = ly - ratio * (ly - this.panY)
    this.scale = newScale
    this._applyTransform()
  }

  zoomIn() {
    if (!this.hasWrapperTarget) return
    this._zoomAround(this.wrapperTarget.offsetWidth / 2, this.wrapperTarget.offsetHeight / 2, 1.25)
  }

  zoomOut() {
    if (!this.hasWrapperTarget) return
    this._zoomAround(this.wrapperTarget.offsetWidth / 2, this.wrapperTarget.offsetHeight / 2, 1 / 1.25)
  }

  fitToViewport() {
    if (!this.hasCanvasTarget || !this.hasWrapperTarget) return
    const vw = this.wrapperTarget.offsetWidth
    const vh = this.wrapperTarget.offsetHeight
    const cw = parseFloat(this.canvasTarget.style.width)  || vw
    const ch = parseFloat(this.canvasTarget.style.height) || vh
    if (!vw || !vh || !cw || !ch) return
    this.scale = Math.min(vw / cw, vh / ch) * 0.9
    this.panX  = (vw - cw * this.scale) / 2
    this.panY  = (vh - ch * this.scale) / 2
    this._applyTransform()
  }

  onPointerDown(event) {
    if (event.button !== 0)                     return
    if (event.target.closest('[data-node-id]')) return
    if (event.target.closest('.ag-note'))       return
    if (event.target.closest('[data-hud]'))     return
    this.wrapperTarget.setPointerCapture(event.pointerId)
    this._panning   = true
    this._panStartX = event.clientX - this.panX
    this._panStartY = event.clientY - this.panY
    this.wrapperTarget.style.cursor = 'grabbing'
  }

  onPointerMove(event) {
    if (!this._panning) return
    this.panX = event.clientX - this._panStartX
    this.panY = event.clientY - this._panStartY
    this._applyTransform()
  }

  onPointerUp(event) {
    if (!this._panning) return
    this._panning = false
    this.wrapperTarget.style.cursor = ''
    if (event?.pointerId != null && this.wrapperTarget.hasPointerCapture(event.pointerId)) {
      this.wrapperTarget.releasePointerCapture(event.pointerId)
    }
  }

  onKeyDown(event) {
    if ((event.metaKey || event.ctrlKey) && event.key === '0') {
      event.preventDefault()
      this.fitToViewport()
    }
  }

  _applyTransform() {
    if (!this.hasCanvasTarget) return
    this.canvasTarget.style.transform = `translate(${this.panX}px, ${this.panY}px) scale(${this.scale})`
    this._updateMinimapViewport()
  }

  // --- Fullscreen ---

  toggleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      this.wrapperTarget.requestFullscreen?.()
    }
  }

  _handleFullscreenChange() {
    if (document.fullscreenElement === this.wrapperTarget) {
      this.wrapperTarget.style.background = '#0d0d14'
    } else {
      this.wrapperTarget.style.background = ''
    }
    requestAnimationFrame(() => this.fitToViewport())
  }

  // --- Node drag ---

  _nodeDragStart(e, el) {
    if (e.button !== 0) return
    e.preventDefault()
    e.stopPropagation()
    el._dragged    = false
    const startX   = e.clientX
    const startY   = e.clientY
    const origLeft = parseFloat(el.style.left) || 0
    const origTop  = parseFloat(el.style.top)  || 0

    const onMove = mv => {
      const dx = (mv.clientX - startX) / this.scale
      const dy = (mv.clientY - startY) / this.scale
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) el._dragged = true
      if (!el._dragged) return
      const newX = origLeft + dx
      const newY = origTop  + dy
      el.style.left = `${newX}px`
      el.style.top  = `${newY}px`
      const id = el.dataset.nodeId
      if (this._positions?.[id]) {
        this._positions[id].x = newX
        this._positions[id].y = newY
      }
      this.drawEdges()
      this._updateMinimap()
    }

    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup',   onUp)
      if (el._dragged && el.dataset.nodeUrl) {
        const pos = this._positions?.[el.dataset.nodeId]
        if (pos) {
          const x = Math.round(pos.x)
          const y = Math.round(pos.y)
          el.dataset.positionX = x
          el.dataset.positionY = y
          this._savePosition(el.dataset.nodeUrl, x, y)
        }
      }
    }

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup',   onUp)
    this._cleanupNodeDrag = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup',   onUp)
    }
  }

  _cleanupNodeDrag() {}

  async _persistPosition(url, x, y) {
    const csrf = document.querySelector('[name="csrf-token"]')?.content
    const body = new FormData()
    body.append('adventure_node[position_x]', x)
    body.append('adventure_node[position_y]', y)
    try {
      await fetch(url, {
        method: 'PATCH',
        headers: { 'X-CSRF-Token': csrf, 'Accept': 'application/json' },
        body
      })
    } catch (_) {}
  }

  // --- Drag-to-connect ---

  _addConnectHandle(el) {
    const handle = document.createElement('div')
    handle.className = 'ag-handle'
    handle.innerHTML = `<svg viewBox="0 0 8 8" width="8" height="8" fill="currentColor"><circle cx="4" cy="4" r="3"/></svg>`
    handle.addEventListener('click',       e => e.stopPropagation())
    handle.addEventListener('mousedown',   e => { e.preventDefault(); e.stopPropagation() })
    handle.addEventListener('pointerdown', e => {
      if (e.button !== 0) return
      e.preventDefault()
      e.stopPropagation()
      this._startConnect(e, el)
    })
    el.appendChild(handle)
  }

  _startConnect(e, fromEl) {
    const fromId = fromEl.dataset.nodeId
    const pos    = this._positions?.[fromId]
    if (!pos) return

    this._connecting = { fromId, fromEl }

    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line')
    line.setAttribute('stroke',           '#c9a96e')
    line.setAttribute('stroke-width',     '1.5')
    line.setAttribute('stroke-dasharray', '5 3')
    line.setAttribute('opacity',          '0.8')
    line.setAttribute('pointer-events',   'none')
    const x1 = pos.x + pos.w
    const y1 = pos.y + pos.h / 2
    line.setAttribute('x1', x1); line.setAttribute('y1', y1)
    line.setAttribute('x2', x1); line.setAttribute('y2', y1)
    this.svgTarget.appendChild(line)
    this._tempLine = line

    this._onConnectMove = ev => this._connectMove(ev)
    this._onConnectEnd  = ev => this._connectEnd(ev)
    document.addEventListener('pointermove',   this._onConnectMove)
    document.addEventListener('pointerup',     this._onConnectEnd)
    document.addEventListener('pointercancel', this._onConnectEnd)
  }

  _connectMove(e) {
    if (!this._connecting || !this._tempLine || !this.hasWrapperTarget) return
    const rect = this.wrapperTarget.getBoundingClientRect()
    const cx   = (e.clientX - rect.left - this.panX) / this.scale
    const cy   = (e.clientY - rect.top  - this.panY) / this.scale
    this._tempLine.setAttribute('x2', cx)
    this._tempLine.setAttribute('y2', cy)

    const target = document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-node-id]')
    this.nodeTargets.forEach(n =>
      n.classList.toggle('ag-connect-target', n === target && n !== this._connecting.fromEl)
    )
  }

  _connectEnd(e) {
    if (!this._connecting) return
    const { fromId } = this._connecting
    this._connecting = null

    document.removeEventListener('pointermove',   this._onConnectMove)
    document.removeEventListener('pointerup',     this._onConnectEnd)
    document.removeEventListener('pointercancel', this._onConnectEnd)
    this._tempLine?.remove()
    this._tempLine = null
    this.nodeTargets.forEach(n => n.classList.remove('ag-connect-target'))

    const toEl = document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-node-id]')
    const toId = toEl?.dataset.nodeId
    if (!toId || toId === fromId) return

    this._showLabelPrompt(e.clientX, e.clientY, fromId, toId)
  }

  _showLabelPrompt(vx, vy, fromId, toId) {
    const prompt = document.createElement('div')
    prompt.className = 'ag-connect-prompt'

    const pw = 290
    const left = Math.max(8, Math.min(vx - pw / 2, window.innerWidth - pw - 8))
    const top  = Math.max(8, Math.min(vy + 12,     window.innerHeight - 52))
    prompt.style.left = `${left}px`
    prompt.style.top  = `${top}px`

    const input = document.createElement('input')
    input.type        = 'text'
    input.placeholder = 'Exit label (optional)'
    input.className   = 'ag-connect-input'

    const connectBtn = document.createElement('button')
    connectBtn.textContent = 'Connect'
    connectBtn.className   = 'btn-gold btn-small'
    connectBtn.type        = 'button'

    const cancelBtn = document.createElement('button')
    cancelBtn.textContent = 'Cancel'
    cancelBtn.className   = 'btn-surface btn-small'
    cancelBtn.type        = 'button'

    let closed = false
    const outsideOff = () => document.removeEventListener('pointerdown', onOutside)

    const doConnect = () => {
      if (closed) return; closed = true
      prompt.remove(); outsideOff()
      this._postEdge(fromId, toId, input.value.trim())
    }
    const doCancel = () => {
      if (closed) return; closed = true
      prompt.remove(); outsideOff()
    }
    const onOutside = e => { if (!prompt.contains(e.target)) doCancel() }

    input.addEventListener('keydown', e => {
      if (e.key === 'Enter')  doConnect()
      if (e.key === 'Escape') doCancel()
    })
    connectBtn.addEventListener('click', doConnect)
    cancelBtn.addEventListener('click',  doCancel)

    prompt.append(input, connectBtn, cancelBtn)
    document.body.appendChild(prompt)
    requestAnimationFrame(() => input.focus())
    setTimeout(() => document.addEventListener('pointerdown', onOutside), 100)
  }

  async _postEdge(fromId, toId, label) {
    if (!this.createEdgeUrlValue) return
    const csrf = document.querySelector('[name="csrf-token"]')?.content
    const body = new FormData()
    body.append('adventure_node_edge[from_node_id]', fromId)
    body.append('adventure_node_edge[to_node_id]',   toId)
    if (label) body.append('adventure_node_edge[label]', label)

    try {
      const res = await fetch(this.createEdgeUrlValue, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrf, 'Accept': 'application/json' },
        body
      })
      if (res.ok) {
        const edge = await res.json()
        this.edgesValue = [...this.edgesValue, { id: edge.id, from: edge.from_node_id, to: edge.to_node_id, label: edge.label || '' }]
      }
    } catch (_) {}
  }

  _cleanupConnect() {
    if (this._onConnectMove) {
      document.removeEventListener('pointermove',   this._onConnectMove)
      document.removeEventListener('pointerup',     this._onConnectEnd)
      document.removeEventListener('pointercancel', this._onConnectEnd)
    }
    this._tempLine?.remove()
    this._tempLine = null
    this._connecting = null
  }

  // --- Layout ---

  layout() {
    const nodes = this.nodeTargets
    if (nodes.length === 0) return

    const NODE_W = 176
    const NODE_H = 60
    const PAD    = 24

    const g = new dagre.graphlib.Graph()
    g.setGraph({ rankdir: 'TB', nodesep: 32, ranksep: 64 })
    g.setDefaultEdgeLabel(() => ({}))

    const nodeIds = new Set(nodes.map(el => el.dataset.nodeId))
    nodes.forEach(el => g.setNode(el.dataset.nodeId, { width: NODE_W, height: NODE_H }))

    nodes.forEach(el => {
      if (el.dataset.parentId && nodeIds.has(el.dataset.parentId)) {
        g.setEdge(el.dataset.parentId, el.dataset.nodeId)
      }
    })

    this.edgesValue.forEach(edge => {
      const from = String(edge.from)
      const to   = String(edge.to)
      if (g.hasNode(from) && g.hasNode(to) && !g.hasEdge(from, to)) {
        g.setEdge(from, to)
      }
    })

    dagre.layout(g)

    this._positions = {}
    let maxX = 0
    let maxY = 0

    nodes.forEach(el => {
      const id = el.dataset.nodeId
      const w  = el.offsetWidth  || NODE_W
      const h  = el.offsetHeight || NODE_H

      // A node with a persisted position (BL-097) is placed there directly rather
      // than at dagre's computed slot -- dagre still lays out every node so
      // ranking/edge routing stay sane, but pinned nodes override the result.
      let left, top
      if (el.dataset.positionX !== undefined && el.dataset.positionY !== undefined) {
        left = parseInt(el.dataset.positionX, 10)
        top  = parseInt(el.dataset.positionY, 10)
      } else {
        const { x: cx, y: cy } = g.node(id)
        left = Math.round(cx - w / 2) + PAD
        top  = Math.round(cy - h / 2) + PAD
      }

      el.style.left = `${left}px`
      el.style.top  = `${top}px`
      this._positions[id] = { x: left, y: top, w, h }
      maxX = Math.max(maxX, left + w)
      maxY = Math.max(maxY, top + h)
    })

    const info    = g.graph()
    const canvasW = Math.max(Math.round(info.width  || 600) + PAD * 2, maxX + PAD)
    const canvasH = Math.max(Math.round(info.height || 300) + PAD * 2, maxY + PAD)
    this.canvasTarget.style.width  = `${canvasW}px`
    this.canvasTarget.style.height = `${canvasH}px`
    if (this.hasSvgTarget) {
      this.svgTarget.setAttribute('width',  canvasW)
      this.svgTarget.setAttribute('height', canvasH)
    }

    this._renderNotes()
    this._updateMinimap()
  }

  // --- Edges ---

  drawEdges() {
    if (!this.hasSvgTarget) return
    this.svgTarget.innerHTML = ''
    this._ensureArrowMarker()

    this.edgesValue.forEach(edge => {
      const from = this._positions?.[String(edge.from)]
      const to   = this._positions?.[String(edge.to)]
      if (!from || !to) return

      const x1 = from.x + from.w / 2
      const y1 = from.y + from.h
      const x2 = to.x   + to.w   / 2
      const y2 = to.y
      const dy = Math.abs(y2 - y1)
      const cp = Math.max(dy * 0.5, 40)

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      path.setAttribute('d', `M ${x1} ${y1} C ${x1} ${y1 + cp}, ${x2} ${y2 - cp}, ${x2} ${y2}`)
      path.setAttribute('stroke',       '#c9a96e')
      path.setAttribute('stroke-width', '1.5')
      path.setAttribute('fill',         'none')
      path.setAttribute('opacity',      '0.6')
      path.setAttribute('marker-end',   'url(#arrowhead)')
      this.svgTarget.appendChild(path)

      if (edge.label) {
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text')
        text.setAttribute('x',           (x1 + x2) / 2)
        text.setAttribute('y',           (y1 + y2) / 2 - 4)
        text.setAttribute('fill',        '#7a7568')
        text.setAttribute('font-size',   '10')
        text.setAttribute('text-anchor', 'middle')
        text.textContent = edge.label
        this.svgTarget.appendChild(text)
      }
    })

    // Re-append temp line if a connect drag is in progress
    if (this._tempLine) this.svgTarget.appendChild(this._tempLine)
  }

  _ensureArrowMarker() {
    const defs   = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
    const marker = document.createElementNS('http://www.w3.org/2000/svg', 'marker')
    marker.setAttribute('id',           'arrowhead')
    marker.setAttribute('markerWidth',  '8')
    marker.setAttribute('markerHeight', '6')
    marker.setAttribute('refX',         '6')
    marker.setAttribute('refY',         '3')
    marker.setAttribute('orient',       'auto')
    const poly = document.createElementNS('http://www.w3.org/2000/svg', 'polygon')
    poly.setAttribute('points',  '0 0, 8 3, 0 6')
    poly.setAttribute('fill',    '#c9a96e')
    poly.setAttribute('opacity', '0.6')
    marker.appendChild(poly)
    defs.appendChild(marker)
    this.svgTarget.insertBefore(defs, this.svgTarget.firstChild)
  }

  // --- Sticky notes ---

  onCanvasDblClick(e) {
    if (e.target.closest('[data-node-id]')) return
    if (e.target.closest('.ag-note'))       return
    if (e.target.closest('[data-hud]'))     return
    if (!this.hasWrapperTarget)             return
    const rect = this.wrapperTarget.getBoundingClientRect()
    const x = Math.round((e.clientX - rect.left - this.panX) / this.scale)
    const y = Math.round((e.clientY - rect.top  - this.panY) / this.scale)
    this._createNote(x, y)
  }

  _renderNotes() {
    if (!this.hasCanvasTarget) return
    this.canvasTarget.querySelectorAll('.ag-note').forEach(el => el.remove())
    this.notesValue.forEach(note => this._appendNote(note))
  }

  _appendNote(note) {
    const el = document.createElement('div')
    el.className     = 'ag-note'
    el.dataset.noteId = note.id
    el.style.left = `${note.x}px`
    el.style.top  = `${note.y}px`

    const body = document.createElement('div')
    body.className       = 'ag-note-body'
    body.textContent     = note.body
    body.contentEditable = this.canConnectValue ? 'true' : 'false'
    body.addEventListener('blur', () => {
      const newBody = body.textContent.trim()
      if (newBody && newBody !== note.body) {
        note.body = newBody
        this._patchNote(note.id, { body: newBody })
      } else {
        body.textContent = note.body
      }
    })
    el.appendChild(body)

    if (this.canConnectValue) {
      const close = document.createElement('button')
      close.type      = 'button'
      close.className = 'ag-note-close'
      close.innerHTML = '&times;'
      close.addEventListener('mousedown', e => e.stopPropagation())
      close.addEventListener('click', e => {
        e.stopPropagation()
        this._deleteNote(note.id, el)
      })
      el.appendChild(close)
      el.addEventListener('mousedown', e => this._noteDragStart(e, el, note))
    }

    this.canvasTarget.appendChild(el)
    return el
  }

  _noteDragStart(e, el, note) {
    if (e.button !== 0) return
    if (e.target.closest('.ag-note-close')) return
    // Don't intercept mousedown outright -- a plain click must still place the
    // caret in the contenteditable body. Only take over (and cancel the pending
    // edit/selection) once the pointer actually moves past the drag threshold.
    const body     = el.querySelector('.ag-note-body')
    const startX   = e.clientX
    const startY   = e.clientY
    const origLeft = parseFloat(el.style.left) || 0
    const origTop  = parseFloat(el.style.top)  || 0
    let dragged = false

    const onMove = mv => {
      const dx = (mv.clientX - startX) / this.scale
      const dy = (mv.clientY - startY) / this.scale
      if (!dragged && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
        dragged = true
        body?.blur()
        window.getSelection()?.removeAllRanges()
      }
      if (!dragged) return
      el.style.left = `${origLeft + dx}px`
      el.style.top  = `${origTop  + dy}px`
    }

    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup',   onUp)
      if (dragged) {
        note.x = Math.round(parseFloat(el.style.left))
        note.y = Math.round(parseFloat(el.style.top))
        this._patchNote(note.id, { position_x: note.x, position_y: note.y })
        this._updateMinimap()
      }
    }

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup',   onUp)
  }

  async _createNote(x, y) {
    if (!this.notesUrlValue) return
    const csrf = document.querySelector('[name="csrf-token"]')?.content
    const body = new FormData()
    body.append('adventure_graph_note[body]', 'New note')
    body.append('adventure_graph_note[position_x]', x)
    body.append('adventure_graph_note[position_y]', y)

    try {
      const res = await fetch(this.notesUrlValue, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrf, 'Accept': 'application/json' },
        body
      })
      if (res.ok) {
        const created = await res.json()
        const shaped  = { id: created.id, body: created.body, x: created.position_x, y: created.position_y }
        this.notesValue = [...this.notesValue, shaped]
        const el = this._appendNote(shaped)
        this._updateMinimap()
        requestAnimationFrame(() => {
          const noteBody = el.querySelector('.ag-note-body')
          noteBody.focus()
          document.getSelection()?.selectAllChildren(noteBody)
        })
      }
    } catch (_) {}
  }

  async _patchNote(id, attrs) {
    if (!this.notesUrlValue) return
    const csrf = document.querySelector('[name="csrf-token"]')?.content
    const body = new FormData()
    Object.entries(attrs).forEach(([key, value]) => body.append(`adventure_graph_note[${key}]`, value))

    try {
      await fetch(`${this.notesUrlValue}/${id}`, {
        method: 'PATCH',
        headers: { 'X-CSRF-Token': csrf, 'Accept': 'application/json' },
        body
      })
    } catch (_) {}
  }

  async _deleteNote(id, el) {
    el.remove()
    this.notesValue = this.notesValue.filter(n => n.id !== id)
    this._updateMinimap()
    if (!this.notesUrlValue) return
    const csrf = document.querySelector('[name="csrf-token"]')?.content

    try {
      await fetch(`${this.notesUrlValue}/${id}`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrf, 'Accept': 'application/json' }
      })
    } catch (_) {}
  }

  // --- Minimap ---

  _updateMinimap() {
    if (!this.hasMinimapTarget || !this.hasCanvasTarget) return
    const canvasW = parseFloat(this.canvasTarget.style.width)  || 1
    const canvasH = parseFloat(this.canvasTarget.style.height) || 1
    const mmW = this.minimapTarget.clientWidth
    const mmH = this.minimapTarget.clientHeight
    this._mmScale = Math.min(mmW / canvasW, mmH / canvasH)

    this.minimapTarget.querySelectorAll('.ag-minimap-node').forEach(el => el.remove())

    Object.values(this._positions || {}).forEach(pos => {
      const dot = document.createElement('div')
      dot.className   = 'ag-minimap-node'
      dot.style.left   = `${pos.x * this._mmScale}px`
      dot.style.top    = `${pos.y * this._mmScale}px`
      dot.style.width  = `${Math.max(pos.w * this._mmScale, 3)}px`
      dot.style.height = `${Math.max(pos.h * this._mmScale, 3)}px`
      this.minimapTarget.appendChild(dot)
    })

    this.notesValue.forEach(note => {
      const dot = document.createElement('div')
      dot.className  = 'ag-minimap-node ag-minimap-note'
      dot.style.left  = `${note.x * this._mmScale}px`
      dot.style.top   = `${note.y * this._mmScale}px`
      dot.style.width  = '3px'
      dot.style.height = '3px'
      this.minimapTarget.appendChild(dot)
    })

    this._updateMinimapViewport()
  }

  _updateMinimapViewport() {
    if (!this.hasMinimapViewportTarget || !this.hasWrapperTarget || !this._mmScale) return
    const vw = this.wrapperTarget.offsetWidth  / this.scale
    const vh = this.wrapperTarget.offsetHeight / this.scale
    const vx = -this.panX / this.scale
    const vy = -this.panY / this.scale
    this.minimapViewportTarget.style.left   = `${vx * this._mmScale}px`
    this.minimapViewportTarget.style.top    = `${vy * this._mmScale}px`
    this.minimapViewportTarget.style.width  = `${vw * this._mmScale}px`
    this.minimapViewportTarget.style.height = `${vh * this._mmScale}px`
  }

  onMinimapClick(e) {
    if (!this._mmScale || !this.hasWrapperTarget || !this.hasMinimapTarget) return
    const rect = this.minimapTarget.getBoundingClientRect()
    const cx = (e.clientX - rect.left) / this._mmScale
    const cy = (e.clientY - rect.top)  / this._mmScale
    this.panX = this.wrapperTarget.offsetWidth  / 2 - cx * this.scale
    this.panY = this.wrapperTarget.offsetHeight / 2 - cy * this.scale
    this._applyTransform()
  }
}
